/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { RelativeDate } from '../localization/LocalizedDate';
import { FileSize, FileTypeName } from '../localization/LocalizedFile';

import FileModel from '../../models/FileModel';
import IconAttachment from './icons/IconAttachment';
import { Grid, VerticalCenterGrid } from './Grid';
import { useFileForActiveProfile } from '../hooks/useFile';
import { useAttachmentsForFile } from '../hooks/useAttachments';
import { ParentAttachmentSingleLineSummary } from '../views/components/attributes/Attachments';
import { useActiveUser } from '../hooks/useUser';
import HealthEntryMetaIcons from '../views/components/attributes/HealthEntryMetaIcons';
import { IconSlot } from '../views/components/IconSlot';

enum FileListItemDisplay {
  list = 'list',
  title = 'title',
}

const MAX_SHOWING_COUNT = 2;

interface AttachmentSummaryListProps {
  attachments: any[];
  languageCode: string;
}
const AttachmentSummaryList: React.FC<AttachmentSummaryListProps> = (props) => {
  const { attachments, languageCode } = props;

  const attachmentsToShow = attachments.slice(0, MAX_SHOWING_COUNT);
  const hiddenCount = attachments.length - MAX_SHOWING_COUNT;

  return (
    <div className="font-footnote font-color-gsDark" style={{ display: 'flex' }}>
      <div className="cmp-attached-health-entries-attached" style={{ marginRight: 6 }}>
        <IconAttachment.Small />
      </div>
      <div>
        {
          attachmentsToShow.map((attachment, i) => (
            <div key={i} style={{ marginBottom: 4 }}>
              <ParentAttachmentSingleLineSummary
                attachment={attachment}
                languageCode={languageCode}
              />
            </div>
          ))
        }
        {
          hiddenCount > 0 ? (
            <div className="cmp-attached-health-entries-more font-color-gsMiddle">
              <FormattedMessage id="notes.more.button.label" values={{ digit1: hiddenCount }} />
            </div>
          ) : null
        }
      </div>
    </div>
  );
};

interface Props {
  file: any;
  attachments: any[];
  hideIcon: boolean;
  color: string;
  flaggedStyle: object;
  flaggedClassName: string;
  hideFlagged: boolean;
  hideFileSize: boolean;
  titleClassName: string;
  metadataClassName: string;
  showAttachedList: boolean;
  showAttached: boolean;
  renderFileName?(file: FileModel): any;
  showFileTypeName: boolean;
}
const displayKind = FileListItemDisplay.list;

const FileListItem = (props: Props) => {
  const hasAttachments = props.attachments.length > 0;
  const [, fileActions] = useFileForActiveProfile();
  const [{ preferredLanguageCode }] = useActiveUser();

  const file = fileActions.instantiateFile(props.file);

  const FileIcon = file.icon();
  const mainStyleClass = classnames('cmp-file-list-item', `cmp-filesummary-${displayKind}`, {
    'has-icon': !props.hideIcon,
    'font-color-white': props.color === 'white',
  });

  return (
    <div className={mainStyleClass} style={{ width: '100%' }}>
      <Grid justifyContent="space-between" alignItems="center">
        <div className="file-description">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>

            <div className={classnames('file-title', props.titleClassName)}>
              {props.renderFileName ? props.renderFileName(file) : file.getName()}
            </div>
            <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
              <div style={{ marginTop: 4 }}>
                <HealthEntryMetaIcons
                  hasComments={false}
                  hasAttachments={false}
                  isFlagged={!props.hideFlagged && file.isFlagged()}
                />
              </div>
              {
                props.hideIcon ? null : (
                  <IconSlot>
                    <FileIcon />
                  </IconSlot>
                )
              }
            </div>
          </div>

          <div className={classnames('file-metadata', props.metadataClassName)}>
            <RelativeDate date={file.getObservedDate().date} precision={file.getObservedDate().precision} dateKind="medium" languageCode={preferredLanguageCode} />
            {!props.hideFileSize && <FileSize fileSizeData={file.fileSizeData()} />}
            {props.showFileTypeName && <FileTypeName file={file}/>}
            {(props.showAttached && hasAttachments) && <IconAttachment.Small />}
          </div>
          {(props.showAttachedList && hasAttachments) && <AttachmentSummaryList attachments={props.attachments} languageCode={preferredLanguageCode} />}
        </div>
      </Grid>
    </div>
  );
};

export const ProfileFileListItem = (props) => {
  const [{ attachments }] = useAttachmentsForFile(props.file.id);

  return (
    <FileListItem
      flaggedClassName="bkgd-color-profile-highlight"
      hideFlagged={false}
      showAttachedList={true}
      titleClassName="font-body is-one-line"
      metadataClassName={classnames('font-footnote', 'font-color-gsDark')}
      attachments={attachments}
      {...props}
    />
  );
};

export const ProfileFileListItemFlaggedRight = (props) => {
  const [{ attachments }] = useAttachmentsForFile(props.file.id);

  return (
    <ProfileFileListItem
      flaggedClassName="bkgd-color-profile-highlight cmp-file-list-item-flagged-right"
      showFileTypeName={false}
      attachments={attachments}
      {...props}
    />
  );
};
export const ShareCardSummaryFileListItem = (props) => (
  <FileListItem
    hideIcon
    hideFlagged
    showAttached={false}
    showAttachedList={false}
    titleClassName="font-body is-one-line"
    metadataClassName="font-caption-1"
    attachments={[]}
    {...props}
  />
);

export const ShareCardEditFileListItem = (props) => (
  <FileListItem
    hideIcon
    hideFlagged={false}
    showAttached={false}
    showAttachedList={false}
    titleClassName="font-body is-one-line"
    metadataClassName="font-caption-1"
    flaggedClassName="bkgd-color-profile-highlight"
    attachments={[]}
    {...props}
  />
);

export const PublicShareCardFileListItem = (props) => (
  <FileListItem
    hideIcon
    hideFlagged
    hideFileSize={false}
    showAttached={false}
    showAttachedList={false}
    titleClassName="font-headline"
    metadataClassName="font-subhead"
    color="gsMedDark"
    flaggedStyle={{}}
    flaggedClassName=""
    attachments={[]}
    {...props}
  />
);

export default FileListItem;
